import React from 'react';
import DOMPurify from 'dompurify';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useMutation, useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { set } from 'lodash';
import Autocomplete from 'src/components/Autocomplete';
import { UploadedFileSchema } from 'src/Ghg/CodeOfConductSurvey/types';
import SingleFileUpload from 'src/components/SingleFileUpload';
import { useLcaImage } from 'src/Lca/hooks';
import { useLcaOrganization } from 'src/common/hooks';
import { UUID } from 'crypto';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import GpcCodeAutocomplete from '../../components/GpcCodeAutocomplete';
import MultipleFilesUpload from '../../components/MultipleFilesUpload';
import {
  getProductComparableFieldValue,
  useProductFieldError,
} from '../validation';
import { buildSelectInputWithInfo } from '../fieldInfo';
import { ComplianceField } from 'src/Lca/compliance';
import { getPassportUrl } from 'src/Lca/ProductPassport/utils';
import { hasValidGtinChecksum, hasValidGtinLength } from 'src/Lca/gtin';

// OrganizationUploadedImage accepts raster images only, so no SVG here.
const PASSPORT_QR_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
// below this width an uploaded code tends to blur when printed on a label
const MIN_PASSPORT_QR_WIDTH = 600;

const normalizeUrl = (url: string) =>
  url.trim().replace(/\/+$/, '').toLowerCase();

interface LcaIndustryCode {
  code: string;
  description: string;
}

interface LcaProductGtinOutSchema {
  gtin_code: string;
  name: string;
  description: string;
  image_id: UUID | null;
  gpc_code: string | null;
}

export default function AdditionalInfo({
  setValues,
  values,
}: ProductInformationProps) {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'basicInfo' });
  const productTypeOptions = [
    {
      value: 'Physical',
      label: t('productTypeOptions.physical'),
    },
    {
      value: 'Service',
      label: t('productTypeOptions.service'),
    },
  ];
  const { data: cpcData } = useQuery('getCpc', () => {
    return client.get<LcaIndustryCode[]>('/web/industries/cpc');
  });

  const { data: naceData } = useQuery('getNace', () => {
    return client.get<LcaIndustryCode[]>('/web/industries/nace');
  });

  const organization = useLcaOrganization();

  const naceOptional = values.nace_industries.slice(1);
  const importGtin = useMutation(() =>
    client.post<LcaProductGtinOutSchema>(
      `/web/lca/product-codes/gtin/${values.gtin_code}/import-as-product`,
      { organization_id: organization.id }
    )
  );

  const removeNace = (i: number) => {
    setValues({
      ...values,
      nace_industries: values.nace_industries.splice(i, 1),
    });
  };
  const removeCpc = () => {
    setValues({
      ...values,
      cpc_industry: '',
    });
  };
  const addNace = () => {
    setValues({
      ...values,
      nace_industries: [...values.nace_industries, { code: '' }],
    });
  };
  const handleNace = (value: string | null, i: number) => {
    const temp = { ...values };
    set(temp, `nace_industries[${i}].code`, value);
    setValues(temp);
  };

  const handleCpc = (value: string | null) => {
    setValues({ ...values, cpc_industry: { code: value } });
  };

  const handleDevelopmentFlowchartsDescription = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      development_flowcharts_description: e.target.value,
    });
  };

  const handleTechnicalDocumentationDescription = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      technical_documentation_description: e.target.value,
    });
  };

  const handleRelatedDocumentsDescription = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      related_documents_description: e.target.value,
    });
  };

  const handleProductCategoryRules = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, product_category_rules: e.target.value });
  };

  const handleIntendedUse = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, actual_product_use_information: e.target.value });
  };
  const handleAdditionalInformation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, additional_information: e.target.value });
  };
  const handleProductionDate = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, production_date: e.target.value || null });
  };
  const handleWarrantyPeriodConditions = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, warranty_period_conditions: e.target.value });
  };
  const handleProductType = (value: string) => {
    setValues({ ...values, product_type: value });
  };

  const handleCeMark = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, ce_mark: e.target.checked });
  };

  const handleImageAdd = (image: UploadedFileSchema) => {
    setValues({ ...values, image_id: image.id });
  };
  const handleImageRemove = () => {
    setValues({ ...values, image_id: null });
  };

  const { image } = useLcaImage(values.image_id ?? '');

  const handlePassportQrAdd = (uploadedImage: UploadedFileSchema) => {
    setValues({ ...values, passport_qr_image_id: uploadedImage.id });
  };
  const handlePassportQrRemove = () => {
    setValues({ ...values, passport_qr_image_id: null });
  };
  const handlePassportQrTargetUrl = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, passport_qr_target_url: e.target.value });
  };

  const { image: passportQrImage } = useLcaImage(
    values.passport_qr_image_id ?? ''
  );
  const [isPassportQrTooNarrow, setIsPassportQrTooNarrow] =
    React.useState(false);

  const passportQrImageUrl = passportQrImage?.url;
  React.useEffect(() => {
    if (!passportQrImageUrl) {
      setIsPassportQrTooNarrow(false);
      return;
    }

    const probe = new Image();
    probe.onload = () =>
      setIsPassportQrTooNarrow(probe.naturalWidth < MIN_PASSPORT_QR_WIDTH);
    probe.onerror = () => setIsPassportQrTooNarrow(false);
    probe.src = passportQrImageUrl;
  }, [passportQrImageUrl]);

  const passportUrl = values.passport_access_token
    ? getPassportUrl(values.passport_access_token)
    : null;

  const handleCopyPassportUrl = async () => {
    if (!passportUrl) return;
    try {
      await navigator.clipboard.writeText(passportUrl);
      toast.success(t('passportQrUrlCopied') as string);
    } catch {
      toast.error(t('passportQrUrlCopyFailed') as string);
    }
  };

  const passportQrTargetUrl = (values.passport_qr_target_url || '').trim();
  const hasPassportQrTargetMismatch =
    !!passportQrTargetUrl &&
    !!passportUrl &&
    normalizeUrl(passportQrTargetUrl) !== normalizeUrl(passportUrl);

  const handleGtinCode = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rawValue = e.target.value;
    // Keep only digits
    const sanitizedValue = rawValue.replace(/\D/g, '');
    setValues({ ...values, gtin_code: sanitizedValue ?? '' });
  };

  const getGtinErrorMessage = () => {
    if (!values.gtin_code) return null;
    if (!hasValidGtinLength(values.gtin_code)) {
      return t('errorGtinLength');
    }
    if (!hasValidGtinChecksum(values.gtin_code)) {
      return t('invalidGtinCode');
    }
    return null;
  };

  const invalidGtinMessage = getGtinErrorMessage();
  const isInvalidGtin = !!invalidGtinMessage;
  const naceIndustriesError = useProductFieldError(
    'nace_industries',
    getProductComparableFieldValue(values as any, 'nace_industries')
  );
  const cpcIndustryError = useProductFieldError(
    'cpc_industry',
    getProductComparableFieldValue(values as any, 'cpc_industry')
  );

  const importGtinCode = () => {
    if (isInvalidGtin) return;
    importGtin
      .mutateAsync()
      .then(({ data }) => {
        setValues({
          ...values,
          name: data.name,
          description: data.description,
          image_id: data.image_id,
          gpc_code: data.gpc_code,
        });
        toast.success(t('gtinImported') as string);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          toast.error(t('gtinNotFound') as string);
        } else {
          toast.error(t('gtinImportFailed') as string);
        }
      });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      <ComplianceField
        excelRow={25}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('developmentFlowchartsDescription')}
          name="development_flowcharts_description"
          value={values.development_flowcharts_description}
          onChange={handleDevelopmentFlowchartsDescription}
          multiline
          rows={3}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={26}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <MultipleFilesUpload
          attachmentType="development-flowchart"
          productId={values.id}
        />
      </ComplianceField>
      <FormControl fullWidth sx={{ mt: '16px' }}>
        <Autocomplete
          label={t('mainNaceCode')}
          placeholder={t('mainNaceCode')}
          options={naceData?.data.map((option: LcaIndustryCode) => ({
            value: option.code,
            label: `${option.code} ${option.description}`,
          }))}
          value={values.nace_industries[0].code ?? ''}
          onChange={(value: string | null) => handleNace(value, 0)}
          sx={{
            backgroundColor:
              (values.nace_industries[0].code ?? '') ? '#F6F6F6' : 'inherit',
          }}
          handleClearClick={() =>
            setValues({
              ...values,
              nace_industries: [...values.nace_industries, { code: '' }],
            })
          }
          inputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <Typography variant="captionText">
                    {t('codes.NACE')}
                  </Typography>
                }
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
          errorText={naceIndustriesError}
        />
      </FormControl>
      {naceOptional.length >= 1 &&
        naceOptional.map((_, i: number) => (
          <div
            key={i}
            style={{ display: 'flex', alignItems: 'center', gap: 24 }}
          >
            <FormControl fullWidth>
              <Autocomplete
                label={t('optionalAdditionalNaceCodes')}
                placeholder={t('optionalAdditionalNaceCodes')}
                showInfoIcon
                infoText={t('codes.NACE')}
                options={naceData?.data.map((option: LcaIndustryCode) => ({
                  value: option.code,
                  label: `${option.code} ${option.description}`,
                }))}
                value={naceOptional[i].code}
                onChange={(value: string | null) => handleNace(value, i + 1)}
                sx={{
                  backgroundColor:
                    (values.nace_industries[0].code ?? '')
                      ? '#F6F6F6'
                      : 'inherit',
                }}
                handleClearClick={() => removeNace(i)}
                errorText={naceIndustriesError}
              />
            </FormControl>
            <Button
              variant="secondary"
              size="small"
              onClick={() => removeNace(i)}
            >
              {t('remove')}
            </Button>
          </div>
        ))}
      <div>
        <Button
          variant="secondary"
          size="small"
          onClick={addNace}
          disabled={
            values.nace_industries.length > 9 ||
            values.nace_industries[values.nace_industries.length - 1]?.code ===
              ''
          }
        >
          {t('addNaceCode')}
        </Button>
      </div>
      <FormControl fullWidth>
        <Autocomplete
          label={t('unCpcCode')}
          placeholder={t('unCpcCode')}
          options={cpcData?.data.map((option: LcaIndustryCode) => ({
            value: option.code,
            label: `${option.code} ${option.description}`,
          }))}
          value={values.cpc_industry?.code ?? ''}
          onChange={(value: string | null) => handleCpc(value)}
          sx={{
            backgroundColor:
              (values.cpc_industry.code ?? '') ? '#F6F6F6' : 'inherit',
          }}
          handleClearClick={removeCpc}
          errorText={cpcIndustryError}
          inputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <Typography variant="captionText">
                    {t('codes.CPC')}
                  </Typography>
                }
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </FormControl>
      <ComplianceField
        excelRow={17}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StyledTextField
            showInfoIcon={false}
            InputProps={{
              endAdornment: (
                <Tooltip
                  title={
                    <>
                      <Typography variant="captionText">
                        {t('codes.GTIN')}
                      </Typography>
                      <Link
                        href="https://gs1pl.org/"
                        target="_blank"
                        rel="noopener"
                        underline="none"
                      >
                        <Typography color="white" variant="captionText">
                          {' '}
                          {t('codes.learnMore')}
                        </Typography>
                      </Link>
                    </>
                  }
                  arrow
                >
                  <IconButton>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
            fullWidth
            label={t('gtinCodeOptional')}
            name="gtin"
            value={values.gtin_code}
            onChange={handleGtinCode}
            error={!!invalidGtinMessage}
            helperText={invalidGtinMessage}
          />

          <Button
            variant="secondary"
            size="small"
            onClick={importGtinCode}
            disabled={!values.gtin_code || isInvalidGtin}
          >
            {t('importData')}
          </Button>
        </Box>
      </ComplianceField>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="h6">{t('passportQrCode')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('passportQrHelper')}
        </Typography>
        {passportUrl && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {passportUrl}
            </Typography>
            <Button
              variant="secondary"
              size="small"
              onClick={handleCopyPassportUrl}
            >
              {t('passportQrCopyUrl')}
            </Button>
          </Box>
        )}
        {passportQrImage && (
          <Box
            component="img"
            src={passportQrImage.url}
            alt={t('passportQrPreviewAlt')}
            sx={{
              objectFit: 'contain',
              maxWidth: '180px',
              maxHeight: '180px',
            }}
          />
        )}
        <SingleFileUpload
          url={`/web/organizations/${organization.id}/images`}
          attachButtonText={t('uploadPassportQr')}
          acceptedFileTypes={PASSPORT_QR_FILE_TYPES}
          onSuccess={handlePassportQrAdd}
          onRemoved={handlePassportQrRemove}
          file={passportQrImage}
        />
        {isPassportQrTooNarrow && (
          <Alert severity="warning">{t('passportQrLowResolution')}</Alert>
        )}
        <StyledTextField
          fullWidth
          label={t('passportQrTargetUrlOptional')}
          name="passport_qr_target_url"
          value={values.passport_qr_target_url}
          onChange={handlePassportQrTargetUrl}
        />
        {hasPassportQrTargetMismatch && (
          <Alert severity="warning">{t('passportQrTargetUrlMismatch')}</Alert>
        )}
      </Box>
      <FormControl fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GpcCodeAutocomplete
            inputProps={{
              endAdornment: (
                <Tooltip
                  title={
                    <>
                      <Typography variant="captionText">
                        {t('codes.GPC')}
                      </Typography>
                      <Link
                        href="https://gs1pl.org/"
                        target="_blank"
                        rel="noopener"
                        underline="none"
                      >
                        <Typography color="white" variant="captionText">
                          {' '}
                          {t('codes.learnMore')}
                        </Typography>
                      </Link>
                    </>
                  }
                  arrow
                >
                  <IconButton>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
            value={values.gpc_code}
            onChange={(code) =>
              setValues({ ...values, gpc_code: code?.code ?? null })
            }
          />
        </Box>
      </FormControl>
      <ComplianceField
        excelRow={19}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <FormControl fullWidth>
          <InputLabel id="product-type-label">{t('productType')}</InputLabel>
          <Select
            labelId="product-type-label"
            label={t('productType')}
            value={values.product_type || ''}
            input={buildSelectInputWithInfo({
              label: t('productType'),
              title: i18n.t('lca:fieldInfoFallback', {
                field: t('productType'),
              }),
            })}
            onChange={(e) => handleProductType(e.target.value)}
          >
            {productTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </ComplianceField>
      <ComplianceField
        excelRow={20}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('intendedUse')}
          name="actual_product_use_information"
          value={values.actual_product_use_information}
          onChange={handleIntendedUse}
          multiline
          rows={3}
        />
      </ComplianceField>
      <StyledTextField
        fullWidth
        label={t('additionalInformation')}
        name="additional_information"
        value={values.additional_information}
        onChange={handleAdditionalInformation}
        multiline
        rows={3}
      />
      <ComplianceField
        excelRow={22}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('productionDate')}
          name="production_date"
          type="date"
          value={values.production_date || ''}
          onChange={handleProductionDate}
          InputLabelProps={{ shrink: true }}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={23}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('warrantyPeriodConditions')}
          name="warranty_period_conditions"
          value={values.warranty_period_conditions}
          onChange={handleWarrantyPeriodConditions}
          multiline
          rows={3}
        />
      </ComplianceField>
      {image && (
        <Box
          component="img"
          src={image.url}
          sx={{
            objectFit: 'contain',
            maxWidth: '400px',
            maxHeight: '400px',
          }}
        />
      )}
      <ComplianceField
        excelRow={31}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <SingleFileUpload
          url={`/web/organizations/${organization.id}/images`}
          attachButtonText={t('uploadProductImage')}
          onSuccess={handleImageAdd}
          onRemoved={handleImageRemove}
          file={image}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={27}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          showInfoIcon={false}
          fullWidth
          label={t('technicalDocumentationDescription')}
          name="technical_documentation_description"
          value={values.technical_documentation_description}
          onChange={handleTechnicalDocumentationDescription}
          multiline
          rows={3}
          InputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <Typography
                    variant="captionText"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        t('technicalDocumentationTooltip')
                      ),
                    }}
                  />
                }
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={28}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <MultipleFilesUpload
          attachmentType="technical-documentation"
          productId={values.id}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={29}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('relatedDocumentsDescription')}
          name="related_documents_description"
          value={values.related_documents_description}
          onChange={handleRelatedDocumentsDescription}
          multiline
          rows={3}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={30}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <MultipleFilesUpload
          attachmentType="related-documents"
          productId={values.id}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={24}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>{t('CeMark')}</Typography>
          <Switch checked={values.ce_mark} onChange={handleCeMark} />
          <Tooltip
            title={
              <Typography variant="captionText">
                {t('ceMarkingTooltip')}
              </Typography>
            }
            arrow
          >
            <IconButton>
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </ComplianceField>
      <ComplianceField
        excelRow={32}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          showInfoIcon={false}
          fullWidth
          label={t('productCategoryRules')}
          name="product_category_rules"
          value={values.product_category_rules}
          onChange={handleProductCategoryRules}
          InputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <Typography variant="captionText">
                    {t('productCategoryRulesTooltip')}
                  </Typography>
                }
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </ComplianceField>
    </div>
  );
}
